/**
 * Confidence scorer. Combines a header-match signal (0..1, from the synonym +
 * distance matcher) with a value-evidence signal (0..1, the fraction of a
 * column's values that satisfy a role's recognizer) into a single rankable
 * score plus a high/medium/low band.
 *
 * The load-bearing rule (R-1, acceptance criterion 4): strong VALUE evidence
 * must be able to OVERRIDE a weak or misleading header — a column titled
 * "Territory" whose values are all 2-letter state codes should still resolve to
 * State. Roles with no value recognizer (city, street, address, country, label)
 * pass `valueScore: null` and rest on the header alone.
 */

import type { MatchConfidence } from "./types";

export interface RoleSignal {
  /** Header-name match strength, 0..1 (1 = exact synonym alias). */
  headerScore: number;
  /** Value-evidence strength, 0..1, or null when the role has no recognizer. */
  valueScore: number | null;
}

export interface RoleScore {
  /** Combined 0..1 score for ranking candidate roles against a column. */
  score: number;
  confidence: MatchConfidence;
  /** True when strong value evidence carried a weak/misleading header. */
  overrodeHeader: boolean;
}

const clamp01 = (n: number): number => (n < 0 ? 0 : n > 1 ? 1 : n);

/** Value evidence at/above this fraction is treated as decisive. */
export const STRONG_VALUE = 0.9;
/** Header match below this is "weak" — value evidence may override it. */
export const WEAK_HEADER = 0.5;

export function scoreRole({ headerScore, valueScore }: RoleSignal): RoleScore {
  const h = clamp01(headerScore);
  const v = valueScore === null ? null : clamp01(valueScore);

  const overrodeHeader = v !== null && v >= STRONG_VALUE && h < WEAK_HEADER;

  let score: number;
  if (v === null) {
    score = h;
  } else {
    const blended = 0.5 * h + 0.5 * v;
    // Strong value evidence floors the score so it beats a no-match even when
    // the header disagrees (this is what lets values override the header).
    score = v >= STRONG_VALUE ? Math.max(blended, 0.8) : blended;
  }

  let confidence: MatchConfidence;
  if ((v !== null && v >= STRONG_VALUE && h >= 0.6) || (v === null && h >= 0.85)) {
    confidence = "high";
  } else if (score >= 0.5) {
    confidence = "medium";
  } else {
    confidence = "low";
  }

  return { score, confidence, overrodeHeader };
}
